/**
 * Path handling for the virtual workspace, and where traversal is refused
 * (security control 4). Paths arrive from files dragged in and from share
 * links; neither may reach outside the workspace.
 */

type PathRejection =
  'empty' | 'absolute' | 'escapes-root' | 'too-deep' | 'too-long' | 'invalid-character';

export class InvalidPathError extends Error {
  constructor(
    readonly path: string,
    readonly rejection: PathRejection,
  ) {
    super(`Invalid path ${JSON.stringify(path)}: ${rejection}`);
    this.name = 'InvalidPathError';
  }
}

const MAX_PATH_LENGTH = 1024;
const MAX_PATH_DEPTH = 32;

// Control characters have no place in a filename and can disguise a path.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * Normalises a path and confines it to the workspace root. A path that would
 * climb above the root is refused rather than clamped: silently rewriting it
 * is how a traversal check becomes a traversal bug.
 */
export function normalisePath(input: string): string {
  if (input.length > MAX_PATH_LENGTH) throw new InvalidPathError(input, 'too-long');
  if (CONTROL_CHARACTERS.test(input)) throw new InvalidPathError(input, 'invalid-character');

  const unified = input.replace(/\\/g, '/');
  if (unified.startsWith('/') || /^[a-zA-Z]:\//.test(unified)) {
    throw new InvalidPathError(input, 'absolute');
  }

  const resolved: string[] = [];
  for (const segment of unified.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment !== '..') {
      resolved.push(segment);
      continue;
    }
    if (resolved.length === 0) throw new InvalidPathError(input, 'escapes-root');
    resolved.pop();
  }

  if (resolved.length === 0) throw new InvalidPathError(input, 'empty');
  if (resolved.length > MAX_PATH_DEPTH) throw new InvalidPathError(input, 'too-deep');
  return resolved.join('/');
}

/** The lowercase extension including the dot, or '' when there is none. */
export function extname(path: string): string {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const index = name.lastIndexOf('.');
  return index <= 0 ? '' : name.slice(index).toLowerCase();
}
